import type { LauncherHome, LauncherStatus } from "./launcherApi.js";

/** One tile of the launcher's box row. */
export type BoxTile =
  | { kind: "quick"; label: string }
  | { kind: "settings"; label: string }
  | { kind: "app"; label: string; package: string }
  | { kind: "input"; label: string; id: string; state: "connected" | "standby" };

const PLAY_STORE = "com.android.vending";
const DOWNLOADER = "com.esaba.downloader";

/**
 * The box row, in the owner's order (2026-10-20: "have quick settings before android
 * settings"): Quick settings, Android Settings, Play Store, any other store on the box,
 * Downloader, then the inputs with something plugged in.
 *
 * Only what this box can open is offered, so no tile is a dead button. An input that
 * reports nothing connected is left out: firmware lists ports the box does not have (the
 * projector shows three HDMI and has one), and switching to an empty input shows nothing.
 */
export function boxTiles(home: Pick<LauncherHome, "tiles" | "inputs" | "stores">): BoxTile[] {
  const tiles: BoxTile[] = [{ kind: "quick", label: "Quick settings" }];
  if (home.tiles.settings) tiles.push({ kind: "settings", label: "Android settings" });
  if (home.tiles.playStore) tiles.push({ kind: "app", label: "Play Store", package: PLAY_STORE });
  for (const store of home.stores ?? []) tiles.push({ kind: "app", label: store.label, package: store.package });
  if (home.tiles.downloader) tiles.push({ kind: "app", label: "Downloader", package: DOWNLOADER });
  for (const input of home.inputs) {
    if (input.state === "disconnected") continue;
    tiles.push({ kind: "input", label: input.label || input.type.toUpperCase(), id: input.id, state: input.state });
  }
  return tiles;
}

/** "Wi-Fi ▮▮▮▯", "Ethernet", "No network": the network part of the status line. */
export function networkLabel(status: LauncherStatus): string {
  if (status.transport === "ethernet") return "Ethernet";
  if (status.transport === "wifi") {
    const bars = Math.max(0, Math.min(4, status.wifiSignalLevel ?? 0));
    return `Wi-Fi ${"▮".repeat(bars)}${"▯".repeat(4 - bars)}`;
  }
  if (status.transport === "none") return "No network";
  return "Connected";
}

/** The launcher's status line: clock and date, network, Bluetooth. */
export function statusLine(status: LauncherStatus, now: Date): string {
  const time = now.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  const date = now.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" });
  const parts = [`${time}  ${date}`, networkLabel(status)];
  if (status.bluetoothPresent) parts.push(status.bluetoothOn ? "Bluetooth on" : "Bluetooth off");
  return parts.join("  ·  ");
}

/** "12.4 GB free of 16 GB": storage in the quick panel. */
export function storageLabel(freeBytes: number, totalBytes: number): string {
  const gb = (bytes: number) => {
    const value = bytes / 1024 ** 3;
    return value >= 10 ? `${Math.round(value)} GB` : `${value.toFixed(1)} GB`;
  };
  return `${gb(freeBytes)} free of ${gb(totalBytes)}`;
}
